/**
 * <ta-session-picker> — segmented mode control + contextual session dropdowns.
 *
 * Three modes:
 *   All       (default — current chart behavior, all sessions overlaid)
 *   Single    (one session-id dropdown — drill into one session only)
 *   Compare 2 (two dropdowns — overlay A vs B; B excludes A's selection)
 *
 * Set the session list via `.sessions = [{ id, label, count, started_at }, ...]`
 * (sorted DESC; the first item is the most recent). The picker is hidden
 * when there are 0 sessions; with exactly 1 session the picker renders
 * but Single/Compare-2 modes are disabled with a tooltip.
 *
 * Emits a `session-filter-change` CustomEvent on every state change with
 * detail = { mode, sessionA, sessionB }. Parent main.js listens and pushes
 * the filter onto the chart components and manages mutual exclusion with
 * the previous-period compare button.
 */

const MODES = [
  { id: 'all',      label: 'All' },
  { id: 'single',   label: 'Single' },
  { id: 'compare2', label: 'Compare 2' },
];

class TaSessionPicker extends HTMLElement {
  constructor() {
    super();
    this._sessions = [];
    this._mode = 'all';
    this._sessionA = null;
    this._sessionB = null;
    this._labelText = 'Sessions';
  }

  set sessions(value) {
    this._sessions = Array.isArray(value) ? value : [];
    // Reset selections — picker rebuilds whenever the source list changes
    // (e.g. on athlete switch).
    this._mode = 'all';
    this._sessionA = null;
    this._sessionB = null;
    this.render();
  }
  get sessions() { return this._sessions; }

  // Optional override for the leading label ("Sessions" by default).
  set labelText(v) { this._labelText = v ?? 'Sessions'; this.render(); }

  connectedCallback() {
    this.render();
    this.addEventListener('click', this._onClick = (e) => {
      const modeBtn = e.target.closest('[data-session-mode]');
      if (!modeBtn) return;
      if (modeBtn.disabled) return;
      const next = modeBtn.dataset.sessionMode;
      if (next === this._mode) return;
      this._mode = next;
      // When entering Single, default sessionA to the most recent session.
      // When entering Compare 2, default A=most recent, B=second-most-recent.
      if (next === 'single') {
        this._sessionA = this._sessions[0]?.id ?? null;
        this._sessionB = null;
      } else if (next === 'compare2') {
        this._sessionA = this._sessions[0]?.id ?? null;
        this._sessionB = this._sessions[1]?.id ?? null;
      } else {
        this._sessionA = null;
        this._sessionB = null;
      }
      this.render();
      this._emit();
    });

    this.addEventListener('change', this._onChange = (e) => {
      if (e.target.matches('.ta-session-picker__select[data-slot="A"]')) {
        this._sessionA = e.target.value || null;
        // If Compare-2 and B now equals A, clear B so user picks again.
        if (this._mode === 'compare2' && this._sessionB === this._sessionA) {
          this._sessionB = null;
        }
        this.render();
        this._emit();
      } else if (e.target.matches('.ta-session-picker__select[data-slot="B"]')) {
        this._sessionB = e.target.value || null;
        this.render();
        this._emit();
      }
    });
  }

  render() {
    if (!this.isConnected) return;

    if (this._sessions.length === 0) {
      this.innerHTML = '';
      return;
    }

    const onlyOne = this._sessions.length < 2;
    const segmented = MODES.map((m) => {
      const disabled = onlyOne && m.id !== 'all';
      const active = this._mode === m.id;
      const title = disabled ? 'Need 2+ sessions to compare' : '';
      return `<button type="button"
                class="ta-session-picker__mode${active ? ' is-active' : ''}"
                data-session-mode="${m.id}"
                ${disabled ? 'disabled aria-disabled="true"' : ''}
                ${title ? `title="${title}"` : ''}>${m.label}</button>`;
    }).join('');

    let dropdowns = '';
    if (this._mode === 'single') {
      dropdowns = `
        <select class="ta-session-picker__select" data-slot="A" aria-label="Session">
          ${this._optionsHtml(this._sessions, this._sessionA)}
        </select>
      `;
    } else if (this._mode === 'compare2') {
      const aId = this._sessionA;
      const bId = this._sessionB;
      const aOptions = this._sessions.filter((s) => s.id !== bId);
      const bOptions = this._sessions.filter((s) => s.id !== aId);
      dropdowns = `
        <select class="ta-session-picker__select" data-slot="A" aria-label="Session A">
          ${this._optionsHtml(aOptions, aId)}
        </select>
        <span class="ta-session-picker__vs">vs</span>
        <select class="ta-session-picker__select" data-slot="B" aria-label="Session B">
          <option value="" ${bId == null ? 'selected' : ''}>— pick a session —</option>
          ${this._optionsHtml(bOptions, bId)}
        </select>
      `;
    }

    this.innerHTML = `
      <div class="ta-session-picker" role="group" aria-label="${this._labelText}">
        <span class="ta-session-picker__label">${this._labelText}</span>
        <div class="ta-session-picker__modes" role="tablist">
          ${segmented}
        </div>
        ${dropdowns ? `<div class="ta-session-picker__dropdowns">${dropdowns}</div>` : ''}
      </div>
    `;
  }

  _optionsHtml(sessions, selectedId) {
    return sessions
      .map((s) => {
        const sel = s.id === selectedId ? 'selected' : '';
        return `<option value="${s.id}" ${sel}>${escapeHtml(s.label)}</option>`;
      })
      .join('');
  }

  _emit() {
    this.dispatchEvent(new CustomEvent('session-filter-change', {
      bubbles: true,
      composed: true,
      detail: this.currentFilter(),
    }));
  }

  currentFilter() {
    return {
      mode: this._mode,
      sessionA: this._sessionA,
      sessionB: this._sessionB,
    };
  }

  /**
   * Programmatically force the picker into Single mode for a given session.
   * Used by the session-surface coordinator when a row click on the table
   * or the across-sessions chart drills into a session's detail. Caller is
   * responsible for pushing the resulting filter to subscribers — this
   * method does NOT emit `session-filter-change` (it's a programmatic state
   * change, not a user action).
   */
  setSingleSession(sessionId) {
    this._mode = 'single';
    this._sessionA = sessionId;
    this._sessionB = null;
    this.render();
  }
}

function escapeHtml(s) {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

customElements.define('ta-session-picker', TaSessionPicker);
